declare var moment: any;

import { FormStyle, getLocaleMonthNames, TranslationWidth } from '@angular/common';
import {
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  NgbDate,
  NgbDatepicker,
  NgbDatepickerNavigateEvent,
  NgbDateStruct
} from '@ng-bootstrap/ng-bootstrap';
import { DateTimeService } from '@sb-shared/services/date-time.service';
import { Ng1CachedLookupService } from '@sb-shared/shared.module';

type PickerView = 'days' | 'months' | 'years';

@Component({
  selector: 'sb-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  standalone: false
})
export class DatePickerComponent implements OnInit, OnChanges, DoCheck {

  @Input() modelDate: Date | string | null;
  @Output() modelDateChange = new EventEmitter<Date>();

  @Input() required: boolean;
  @Input() minDate: Date | string | null;
  @Input() maxDate: Date | string | null;
  @Input() readonly: boolean;
  @Input() disabled: boolean;
  @Input() skipFormatSetting: boolean;
  @Input() name: string;
  @Input() specificTimeZone: string;

  @Output() onChange = new EventEmitter<void>();

  @ViewChild('dp') datepicker: NgbDatepicker;

  isOpen = false;
  pickerView: PickerView = 'days';
  format = '';

  dateOptions: { formatYear: string; startingDay: number; minDate?: Date; maxDate?: Date } = {
    formatYear: 'yy',
    startingDay: 1
  };
  modelOptions: { timezone?: string } = {};

  ngbDate: NgbDateStruct | null = null;
  ngbMinDate: NgbDateStruct | null = null;
  ngbMaxDate: NgbDateStruct | null = null;
  startDate: NgbDateStruct | null = null;

  currentMonth: number;
  currentYear: number;
  monthPickerYear: number;
  yearGridStart: number;
  readonly yearsPerPage = 20;
  monthNames: string[];

  private previousDate: Date | string | null = undefined;

  constructor(
    private cachedLookupService: Ng1CachedLookupService,
    private dateTime: DateTimeService,
    private elementRef: ElementRef
  ) {
    this.monthNames = Array.from(
      getLocaleMonthNames(this.dateTime.currentLocale, FormStyle.Standalone, TranslationWidth.Wide)
    );
    const today = new Date();
    this.currentMonth = today.getMonth() + 1;
    this.currentYear = today.getFullYear();
    this.monthPickerYear = this.currentYear;
    this.yearGridStart = this.computeYearGridStart(this.currentYear);
  }

  ngOnInit(): void {
    if (!this.skipFormatSetting) {
      Promise.resolve(this.cachedLookupService.getSystemSetting('Organisation_Date_DefaultPickerFormatting'))
        .then((value: string) => {
          this.format = value;
        });
    }

    if (this.modelDate && !(this.modelDate instanceof Date)) {
      this.modelDate = new Date(String(this.modelDate).replace('Z', ''));
    }

    this.ngbDate = this.toNgbDateStruct(this.modelDate as Date);
    this.previousDate = this.cloneDate(this.modelDate);

    if (this.ngbDate) {
      this.currentMonth = this.ngbDate.month;
      this.currentYear = this.ngbDate.year;
      this.monthPickerYear = this.ngbDate.year;
      this.yearGridStart = this.computeYearGridStart(this.ngbDate.year);
      this.startDate = { ...this.ngbDate };
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.minDate) {
      if (!changes.minDate.currentValue) {
        this.minDate = new Date(1900, 1, 1);
      } else if (!(changes.minDate.currentValue instanceof Date)) {
        this.minDate = moment(changes.minDate.currentValue).toDate();
      }
      this.dateOptions.minDate = this.minDate as Date;
      this.ngbMinDate = this.toNgbDateStruct(this.minDate as Date);
    }

    if (changes.maxDate) {
      if (changes.maxDate.currentValue) {
        if (!(changes.maxDate.currentValue instanceof Date)) {
          this.maxDate = moment(changes.maxDate.currentValue).toDate();
        }
        this.dateOptions.maxDate = this.maxDate as Date;
        this.ngbMaxDate = this.toNgbDateStruct(this.maxDate as Date);
      } else {
        this.maxDate = null;
        this.ngbMaxDate = null;
      }
    }

    if (changes.specificTimeZone) {
      this.modelOptions.timezone = changes.specificTimeZone.currentValue;
    }

    if (changes.modelDate && !changes.modelDate.firstChange) {
      if (this.modelDate && !(this.modelDate instanceof Date)) {
        this.modelDate = new Date(String(this.modelDate).replace('Z', ''));
      }
      this.ngbDate = this.toNgbDateStruct(this.modelDate as Date);
      if (this.ngbDate) {
        this.currentMonth = this.ngbDate.month;
        this.currentYear = this.ngbDate.year;
      }
    }

    this.isOpen = false;
  }

  ngDoCheck(): void {
    if (!this.datesEqual(this.modelDate, this.previousDate)) {
      this.handleDateChange();
      this.previousDate = this.cloneDate(this.modelDate);

      if (this.modelDate) {
        const date = this.modelDate as Date;
        this.modelDateChange.emit(date);
        this.onChange.emit();
      } else {
        this.modelDateChange.emit(null);
      }
    }
  }

  open(): void {
    if (this.readonly || this.disabled) {
      return;
    }
    this.pickerView = 'days';
    if (this.ngbDate) {
      this.startDate = { ...this.ngbDate };
      this.monthPickerYear = this.ngbDate.year;
      this.yearGridStart = this.computeYearGridStart(this.ngbDate.year);
    }
    this.isOpen = true;
  }

  close(): void {
    this.isOpen = false;
    this.pickerView = 'days';
  }

  toggle(): void {
    this.isOpen ? this.close() : this.open();
  }

  handleDateChange(): void {
    if (!this.modelDate) {
      this.ngbDate = null;
      return;
    }

    if (this.modelDate instanceof Date) {
      this.modelDate = new Date(this.modelDate);
    } else {
      this.modelDate = new Date(String(this.modelDate).replace('Z', ''));
    }
    this.ngbDate = this.toNgbDateStruct(this.modelDate);
    if (this.ngbDate) {
      this.currentMonth = this.ngbDate.month;
      this.currentYear = this.ngbDate.year;
    }
  }

  onDateSelect(event: NgbDate): void {
    if (!event) {
      return;
    }
    this.modelDate = new Date(event.year, event.month - 1, event.day);
    this.ngbDate = { year: event.year, month: event.month, day: event.day };
    this.currentMonth = event.month;
    this.currentYear = event.year;
  }

  onNavigate(event: NgbDatepickerNavigateEvent): void {
    if (event && event.next) {
      this.currentMonth = event.next.month;
      this.currentYear = event.next.year;
    }
  }

  prev(): void {
    if (this.pickerView === 'days') {
      this.shiftMonth(-1);
    } else if (this.pickerView === 'months') {
      this.monthPickerYear -= 1;
    } else {
      this.yearGridStart -= this.yearsPerPage;
    }
  }

  next(): void {
    if (this.pickerView === 'days') {
      this.shiftMonth(1);
    } else if (this.pickerView === 'months') {
      this.monthPickerYear += 1;
    } else {
      this.yearGridStart += this.yearsPerPage;
    }
  }

  drillUp(): void {
    if (this.pickerView === 'days') {
      this.monthPickerYear = this.currentYear;
      this.pickerView = 'months';
    } else if (this.pickerView === 'months') {
      this.yearGridStart = this.computeYearGridStart(this.monthPickerYear);
      this.pickerView = 'years';
    }
  }

  selectMonth(monthIndex: number): void {
    const month = monthIndex + 1;
    this.currentMonth = month;
    this.currentYear = this.monthPickerYear;
    this.startDate = { year: this.currentYear, month: this.currentMonth, day: 1 };
    this.datepicker?.navigateTo({ year: this.currentYear, month: this.currentMonth });
    this.pickerView = 'days';
  }

  selectYear(year: number): void {
    this.monthPickerYear = year;
    this.pickerView = 'months';
  }

  isMonthDisabled(monthIndex: number): boolean {
    const month = monthIndex + 1;
    const min = this.ngbMinDate;
    const max = this.ngbMaxDate;
    if (min && (this.monthPickerYear < min.year || (this.monthPickerYear === min.year && month < min.month))) {
      return true;
    }
    if (max && (this.monthPickerYear > max.year || (this.monthPickerYear === max.year && month > max.month))) {
      return true;
    }
    return false;
  }

  isCurrentMonth(monthIndex: number): boolean {
    const month = monthIndex + 1;
    return month === this.currentMonth && this.monthPickerYear === this.currentYear;
  }

  isMonthToday(monthIndex: number): boolean {
    const today = new Date();
    return monthIndex === today.getMonth() && this.monthPickerYear === today.getFullYear();
  }

  isYearDisabled(year: number): boolean {
    const min = this.ngbMinDate;
    const max = this.ngbMaxDate;
    if (min && year < min.year) {
      return true;
    }
    if (max && year > max.year) {
      return true;
    }
    return false;
  }

  isCurrentYear(year: number): boolean {
    return year === this.currentYear;
  }

  isYearToday(year: number): boolean {
    return year === new Date().getFullYear();
  }

  get yearGrid(): number[] {
    const out: number[] = [];
    for (let i = 0; i < this.yearsPerPage; i++) {
      out.push(this.yearGridStart + i);
    }
    return out;
  }

  get yearGridLabel(): string {
    const endShort = ((this.yearGridStart + this.yearsPerPage - 1) % 100).toString().padStart(2, '0');
    const startShort = (this.yearGridStart % 100).toString().padStart(2, '0');
    return `${startShort} - ${endShort}`;
  }

  get headerLabel(): string {
    if (this.pickerView === 'days') {
      const monthKey = this.monthNames[this.currentMonth - 1];
      return monthKey ? `${monthKey}__${this.currentYear}` : `${this.currentYear}`;
    }
    if (this.pickerView === 'months') {
      return String(this.monthPickerYear);
    }
    return this.yearGridLabel;
  }

  get currentMonthKey(): string {
    return this.monthNames[this.currentMonth - 1];
  }

  shortYear(year: number): string {
    return (year % 100).toString().padStart(2, '0');
  }

  setToday(): void {
    const today = new Date();
    this.modelDate = today;
    this.ngbDate = this.toNgbDateStruct(today);
    this.currentMonth = today.getMonth() + 1;
    this.currentYear = today.getFullYear();
    this.monthPickerYear = this.currentYear;
    this.datepicker?.navigateTo({ year: this.currentYear, month: this.currentMonth });
    this.close();
  }

  clearDate(): void {
    this.modelDate = null;
    this.ngbDate = null;
    this.close();
  }

  closePicker(): void {
    this.close();
  }

  onInputBlur(value: string | null): void {
    if (!value) {
      this.modelDate = null;
      this.ngbDate = null;
      return;
    }
    const parsed = this.parseUserInput(value);
    if (parsed) {
      this.modelDate = parsed;
      this.ngbDate = this.toNgbDateStruct(parsed);
      if (this.ngbDate) {
        this.currentMonth = this.ngbDate.month;
        this.currentYear = this.ngbDate.year;
      }
    }
  }

  private parseUserInput(value: string): Date | null {
    // Parse using the configured display format. Falling back to `new Date(...)`
    // is unsafe for day-first inputs (e.g. "31/05/2026") — the V8 implementation
    // either rejects them or silently mis-parses ambiguous values like "01/02/2026".
    // moment with strict parsing against a list of candidate formats keeps the
    // semantics consistent with what the user sees in the input.
    const candidates: string[] = [];
    const fromSetting = this.toMomentFormat(this.format);
    if (fromSetting) {
      candidates.push(fromSetting);
    }
    candidates.push('DD/MMMM/YYYY', 'DD/MM/YYYY', 'D/M/YYYY', 'YYYY-MM-DD');

    const parsed = moment(value, candidates, true);
    return parsed.isValid() ? parsed.toDate() : null;
  }

  private toMomentFormat(format: string): string | null {
    if (!format) {
      return null;
    }
    // Angular DatePipe tokens → moment tokens. Replace longest tokens first so
    // partial matches (e.g. `yy` inside `yyyy`) are not double-converted.
    return format
      .replace(/yyyy/g, 'YYYY')
      .replace(/yy/g, 'YY')
      .replace(/y/g, 'Y')
      .replace(/dd/g, 'DD')
      .replace(/d/g, 'D');
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) {
      return;
    }
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen) {
      this.close();
    }
  }

  private shiftMonth(delta: number): void {
    let month = this.currentMonth + delta;
    let year = this.currentYear;
    if (month > 12) {
      month = 1;
      year += 1;
    } else if (month < 1) {
      month = 12;
      year -= 1;
    }
    this.currentMonth = month;
    this.currentYear = year;
    this.datepicker?.navigateTo({ year, month });
  }

  private computeYearGridStart(year: number): number {
    // (year - 1) so a year that is an exact multiple of yearsPerPage (e.g. 2020,
    // or the 1900 fallback) still lands inside its decade window. Without the
    // -1, 2020 would map to the 2021..2040 page and the user couldn't see the
    // year they're drilling up from.
    return Math.floor((year - 1) / this.yearsPerPage) * this.yearsPerPage + 1;
  }

  private toNgbDateStruct(value: Date | null | undefined): NgbDateStruct | null {
    if (!value || !(value instanceof Date) || isNaN(value.getTime())) {
      return null;
    }
    return { year: value.getFullYear(), month: value.getMonth() + 1, day: value.getDate() };
  }

  private cloneDate(value: Date | string | null | undefined): Date | string | null {
    if (value === null || value === undefined) {
      return value as null | undefined;
    }
    if (value instanceof Date) {
      return new Date(value.getTime());
    }
    return value;
  }

  private datesEqual(a: Date | string | null | undefined, b: Date | string | null | undefined): boolean {
    if (a === b) {
      return true;
    }
    if (a == null || b == null) {
      return a == null && b == null;
    }
    if (a instanceof Date && b instanceof Date) {
      return a.getTime() === b.getTime();
    }
    return false;
  }
}
